import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { Place, PunchLocation } from "@/api/types";
import "./PunchLocationLine.css";

/**
 * Where a shift was punched, on one line.
 *
 * WHAT THIS REPLACED, AND WHY. Both screens used to render the words "location stamped"
 * off a boolean. That told a reader a location EXISTS and never where — so the app was
 * capturing a worker's position and then declining to answer the only question it was
 * captured for. Either the coordinates are worth recording and worth reading, or they
 * should not be recorded; recording them and withholding them was the worst of the three.
 * Decision taken 2026-09-08.
 *
 * IT IS TWO POINTS AND SAYS SO. A browser cannot track in the background —
 * `navigator.geolocation` runs only while the page is open — so there is no trail here, and
 * nothing on this line implies one. The promise on the clock screen, "a single stamp — you
 * are not tracked while you work", is still literally true.
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *
 *   - **An embedded map.** It would need an external tile service, an API key, and a
 *     network round trip on a screen that has to work on bad signal in a yard. The link
 *     below costs nothing until somebody taps it.
 *
 * ef-021 — "A place NAME" is no longer on that list. `PunchLocation.startPlace`/
 * `endPlace` (api/types.ts) are resolved LIVE, server-side, against the tenant's
 * currently-active saved places — never stored on the row — so archiving a place falls
 * every punch that used to resolve to it back to a null `startPlace`/`endPlace` on its
 * very next fetch, with nothing about the punch itself re-saved. @ui-check: this
 * component must therefore keep resolving a null `startPlace`/`endPlace` to something
 * other than a bare coordinate — that path is not dead code kept for a case that can no
 * longer happen, it is what an archived place's punches read as forever.
 *
 * ef-037b-4 (ADR 0009 §4) — "the bare `44.4469, -95.7889` link is gone". A null
 * `startPlace` no longer falls back to the raw pair as visible text: it reads as a
 * distance from the nearest of the tenant's OTHER saved places (`useNearbyPlaces` below —
 * the server only ever resolves a place whose radius CONTAINS the point, so "nearest, but
 * outside every radius" has no existing endpoint to ask and is computed here instead), or
 * as a distance-less phrase when none exists to measure against. The link's `href` still
 * carries the real coordinate — nothing about that promise changes, only the words a
 * reader sees before they choose to tap it.
 */
export function PunchLocationLine({
  location,
  /** True once the shift has ended — a RUNNING shift has no second point to compare to. */
  ended,
  /** "you" on the worker's own screen, "they" on the farmer's queue. */
  voice = "third",
}: {
  location: PunchLocation | null;
  ended: boolean;
  voice?: "first" | "third";
}) {
  if (location === null) return null;

  const subject = voice === "first" ? "You clocked in" : "Clocked in";
  const nearby = useNearbyPlaces(voice);
  const start = describeStart(location, nearby);

  return (
    <span className="punch-location" data-moved={movedState(location, ended)}>
      <span className="punch-location__at">
        {subject} at{" "}
        {/* The map link is kept regardless of whether a name resolved — a farmer who
            recognises "the North 40" by name may still want to see exactly where the
            fix landed, and the link always points at the real coordinate either way. */}
        <a
          className="punch-location__coords"
          data-resolved={start.kind}
          href={mapUrl(location.startLat, location.startLon)}
          target="_blank"
          // noreferrer as well as noopener: the referrer would carry this app's URL to a
          // third party alongside a worker's coordinates, and nothing needs it there.
          rel="noreferrer noopener"
        >
          {start.text}
        </a>
      </span>
      <span className="punch-location__moved">{movedText(location, ended)}</span>
    </span>
  );
}

/**
 * What the link says for the START point, when the server did not resolve a `startPlace`.
 *
 * `"place"` never reaches here (the caller already returns the resolved name); this only
 * ever answers `"distance"` (a nearby saved place exists, just outside its own radius) or
 * `"unsaved"` (nothing to measure against — a places-less tenant, a loading/failed fetch,
 * or the worker's own screen, which is never given the place list at all — see
 * `useNearbyPlaces`). `data-resolved` carries which one fired, ui-breadcrumbs §1, so a
 * tester reads the branch taken instead of guessing it from the words.
 */
function describeStart(
  location: PunchLocation,
  nearby: Place[] | null,
): { kind: "place" | "distance" | "unsaved"; text: string } {
  if (location.startPlace) return { kind: "place", text: location.startPlace.name };

  const nearest = nearby && nearby.length > 0 ? nearestOf(nearby, location.startLat, location.startLon) : null;
  if (nearest) {
    return { kind: "distance", text: `${milesText(nearest.miles)} mi from ${nearest.place.name}` };
  }

  // No saved place resolved, and none close enough to name — a bare coordinate must never
  // be the fallback for THIS fallback (ADR 0009 §4's whole reason to exist), so this reads
  // as an honest, distance-less absence instead.
  return { kind: "unsaved", text: "an unsaved location" };
}

/** The tenant's own saved place closest to the given point, however far that is — never
 *  gated by a radius, unlike `PunchLocation.startPlace`/`endPlace`, which already are. */
function nearestOf(places: Place[], lat: number, lon: number): { place: Place; miles: number } | null {
  let best: { place: Place; miles: number } | null = null;
  for (const place of places) {
    const miles = milesBetween(place.lat, place.lon, lat, lon);
    if (best === null || miles < best.miles) best = { place, miles };
  }
  return best;
}

/**
 * The farm's saved places, fetched once and shared by every row on the page rather than
 * once per row. `TimeApprovalQueue.tsx` renders one `PunchLocationLine` per queue entry;
 * a plain per-component `usePlaces()` call would fire one GET per row on every mount. This
 * file is the only one ef-037b-4's file scope allows touching, so the shared cache lives
 * here — module-scoped, not lifted to a parent — rather than in `TimeApprovalQueue.tsx`.
 *
 * SCOPED TO `voice === "third"` (the farmer's queue) ON PURPOSE, never the worker's own
 * screen. `usePlaces.ts`'s own comment: saved places are "Owner-only server-side (403 for
 * an employee login)". `voice === "first"` is exactly `TimeClock.tsx` — fetching there
 * would 403 on every load, and a failed resource load is one Chromium reports to the
 * page's own console as an error, which is precisely what
 * `offline-provenance.spec.ts`'s "no console errors … on the worker's recent days" gate
 * asserts is empty. The worker's own screen falls back to the distance-less phrase for a
 * null `startPlace` instead — not a lesser answer, the one this session is authorized to
 * ask for.
 */
let placesRequest: Promise<Place[]> | null = null;
let placesResolved: Place[] | null = null;

function loadNearbyPlaces(): Promise<Place[]> {
  if (placesResolved) return Promise.resolve(placesResolved);
  if (!placesRequest) {
    placesRequest = demoApi
      .getPlaces()
      .then((places) => {
        placesResolved = places.filter((place) => place.archivedAt === null);
        return placesResolved;
      })
      .catch(() => {
        // Not cached as a failure — an employee session never retries (voice gates the
        // call below before this runs), but nothing here should assume an owner session
        // that hit a transient error is stuck with it for the rest of the page's life.
        placesRequest = null;
        return [];
      });
  }
  return placesRequest;
}

function useNearbyPlaces(voice: "first" | "third"): Place[] | null {
  const [places, setPlaces] = useState<Place[] | null>(placesResolved);

  useEffect(() => {
    if (voice !== "third" || placesResolved) return;
    let cancelled = false;
    void loadNearbyPlaces().then((resolved) => {
      if (!cancelled) setPlaces(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [voice]);

  return voice === "third" ? places : null;
}

/**
 * Below this, the two fixes are the same place.
 *
 * A consumer GPS fix in the open is good to roughly ten metres, and two readings taken
 * hours apart in one yard differ by more than nothing. 0.05 mi is about 80 m — comfortably
 * above that jitter and comfortably below "a different part of the farm", so it does not
 * report a walk to the shed as a move and does not hide one to the next field.
 *
 * IT IS A WORDING THRESHOLD, NOT A FACT. The distance itself is always computed and always
 * available; this only decides which sentence to print. That is why it lives here rather
 * than on the server, which owns the measurement.
 */
const SAME_PLACE_MILES = 0.05;

function movedState(location: PunchLocation, ended: boolean): "unknown" | "same" | "moved" {
  if (!ended || location.milesApart === null) return "unknown";
  return location.milesApart < SAME_PLACE_MILES ? "same" : "moved";
}

function movedText(location: PunchLocation, ended: boolean): string {
  // A shift still running has one point. Saying anything about a second would be measuring
  // against an end that has not happened.
  if (!ended) return "";

  // NOT "in the same place". The end fix is missing, which is an ordinary thing — a shed
  // with no signal, a declined permission on the way out — and reporting it as zero
  // distance would be a claim about a location nobody recorded. A resolved endPlace
  // implies a real endLat/endLon, so this null-check still runs first regardless.
  if (location.milesApart === null) return " · no fix on the way out";

  // ef-021 — named, same wording shape as the un-named branch below ("clocked out AT
  // the Shop, 4.0 mi away") rather than a second sentence, so the two read as one voice.
  // Distance is still said even when named: "at the Shop" answers WHICH place, the
  // figure answers how far that was from where the shift started, and a farmer reading
  // only the distance would lose the fact that the far end had a name at all.
  if (location.endPlace) {
    if (location.milesApart < SAME_PLACE_MILES) return ` · clocked out at ${location.endPlace.name}`;
    const miles = milesText(location.milesApart);
    return ` · clocked out at ${location.endPlace.name}, ${miles} mi away`;
  }

  if (location.milesApart < SAME_PLACE_MILES) return " · clocked out in the same place";

  return ` · clocked out ${milesText(location.milesApart)} mi away`;
}

/** One decimal under 10 miles, whole miles above — a farm-scale distance never needs
 *  more precision than that, and a bare integer past ten reads cleaner. */
function milesText(miles: number): string {
  return miles < 10 ? miles.toFixed(1) : String(Math.round(miles));
}

/**
 * Great-circle miles between two points — mirrors `PunchDistance.MilesBetween`
 * (`EasyFarm.Domain`), same Earth radius, same formula, deliberately duplicated rather
 * than shared. ADR 0001's "one implementation, shared by server and client" reasoning
 * assumes a runtime that reaches both C# and TypeScript; there isn't one here, and
 * `PlaceResolver.Resolve` on the server only ever answers "which place's radius CONTAINS
 * this point" — it has no "nearest, regardless of radius" endpoint this line could call
 * instead. Not rounded, unlike its C# counterpart: this is an intermediate value `milesText`
 * rounds for display, never a number shown or compared on its own.
 */
const EARTH_RADIUS_MILES = 3958.7613;

function milesBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

/**
 * OpenStreetMap, and no key.
 *
 * Chosen over an embedded map and over a keyed provider for the same reason: this must not
 * add a credential to deploy or a request the page makes on its own. NOTHING IS SENT
 * ANYWHERE UNTIL SOMEBODY TAPS IT — the coordinates travel to osm.org in that URL, which is
 * a real disclosure of a worker's position and is the reader's deliberate act, not a
 * background one. Offline the link simply does not resolve; the row still renders.
 */
function mapUrl(lat: number, lon: number): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
}
